import { format } from 'date-fns';
import { fr, nl, enUS } from 'date-fns/locale';
import { localeForLanguage } from './locale.js';
/**
 * Parses a local ISO date string (YYYY-MM-DD) into a Date object in local timezone
 * This prevents UTC shifts when parsing date-only strings
 */
export function parseLocalISODate(dateString) {
    if (!dateString)
        return undefined;
    // Parse as local date by using Date constructor with year, month, day
    const [year, month, day] = dateString.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day))
        return undefined;
    return new Date(year, month - 1, day); // month is 0-indexed
}
/**
 * Formats a Date object to local ISO date string (YYYY-MM-DD)
 * This ensures dates are saved in the expected format without timezone shifts
 */
export function formatLocalISODate(date) {
    if (!date || isNaN(date.getTime()))
        return null;
    return format(date, 'yyyy-MM-dd');
}
/**
 * Safely formats a date value (date-only string, ISO timestamp, or Date) for display.
 * Returns `fallback` (default '—') when the value is null, undefined, or unparseable.
 *
 * Date-only strings (YYYY-MM-DD) are parsed in the local timezone via
 * `parseLocalISODate` to avoid UTC shifts; everything else falls through to
 * `new Date(...)`.
 *
 * Pass a date-fns `locale` (e.g. from `getDateFnsLocale(i18n.language)`) to
 * localize month/day names; omit it for the default en-US output.
 */
export function safeFormatDate(value, pattern, fallback = '—', locale) {
    if (!value)
        return fallback;
    let date;
    if (value instanceof Date) {
        date = value;
    }
    else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const parsed = parseLocalISODate(value);
        if (!parsed)
            return fallback;
        date = parsed;
    }
    else {
        date = new Date(value);
    }
    if (isNaN(date.getTime()))
        return fallback;
    return format(date, pattern, locale ? { locale } : undefined);
}
const DATE_FNS_LOCALES = { fr, nl, en: enUS };
/** Get the date-fns locale matching the given i18next language code. */
export function getDateFnsLocale(language) {
    return DATE_FNS_LOCALES[language.slice(0, 2)] ?? enUS;
}
const INSTANT_PATTERNS = { date: 'PP', dateTime: 'PPp', time: 'p' };
const INSTANT_INTL_OPTIONS = {
    date: { dateStyle: 'medium' },
    dateTime: { dateStyle: 'medium', timeStyle: 'short' },
    time: { timeStyle: 'short' },
};
/** `Intl.DateTimeFormat` instances are expensive to build; one per zone is plenty. */
const zonedPartsFormatters = new Map();
function zonedPartsFormatter(timeZone) {
    const cached = zonedPartsFormatters.get(timeZone);
    if (cached)
        return cached;
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        // `hourCycle: 'h23'` keeps midnight at 0 rather than the 24 some ICU
        // builds emit for `hour12: false`.
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    zonedPartsFormatters.set(timeZone, formatter);
    return formatter;
}
/** Read an instant's wall-clock fields as they read in `timeZone`. */
function wallClockIn(date, timeZone) {
    const parts = zonedPartsFormatter(timeZone).formatToParts(date);
    const read = (type) => {
        const part = parts.find((candidate) => candidate.type === type);
        return part ? Number(part.value) : 0;
    };
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: read('hour'),
        minute: read('minute'),
        second: read('second'),
    };
}
/**
 * Formats an **instant** (a `timestamptz` value or a `Date`) in an explicit
 * IANA timezone, so the same value renders identically no matter what timezone
 * the rendering process happens to run in.
 *
 * This is the counterpart to `safeFormatDate`, which resolves a value against
 * the *process* timezone. That is correct for `YYYY-MM-DD` date columns (they
 * carry no instant at all) but wrong for `timestamptz` columns: the same row
 * prints "May 30" on a browser in Amsterdam and "May 29" on a UTC server, so a
 * document's screen rendering and its server-printed PDF disagree on a date the
 * document is legally judged by. Pinning to the organization's timezone makes
 * both agree.
 *
 * No document date reaches this function any more:
 * `accounts_receivable.invoice_date`, `accounts_payable.invoice_date` and
 * `ledger_transactions.transaction_date` (a payment's date on an invoice) were
 * the original motivation, and all three are now plain `date` columns. Callers
 * route `YYYY-MM-DD` values to `safeFormatDate` instead (see `isDateOnly` in
 * `packages/documents/.../formatters.ts`) — this function does **not** special-
 * case them: `new Date('2026-03-15')` is UTC midnight, so a date-only string
 * handed here would resolve a day early in any UTC− zone. It stays for the
 * genuine `timestamptz` values a document may yet need to print.
 *
 * Output is produced by date-fns with the same `PP` / `PPp` / `p` patterns as
 * `safeFormatDate`, so the rendered form is unchanged in every language — only
 * the *instant → calendar day* resolution moves. `Intl` is used solely to read
 * the wall clock in `timeZone`.
 *
 * @param timeZone IANA zone (typically `organizations.timezone`). Null,
 *   undefined, or an unknown/garbage zone falls back to UTC rather than
 *   throwing — `Intl` raises `RangeError` on a bad zone, and a stray value in
 *   one org's settings must never take a document render down.
 * @param language i18next language code; drives the date-fns locale.
 */
export function formatInstantInZone(value, timeZone, language, style = 'date', fallback = '—') {
    if (!value)
        return fallback;
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime()))
        return fallback;
    let zone = timeZone ?? 'UTC';
    let wall;
    try {
        wall = wallClockIn(date, zone);
    }
    catch {
        // Unknown IANA zone. UTC is the safe universal fallback: it is the zone the
        // database stores instants in and the zone the PDF renderer runs in.
        zone = 'UTC';
        wall = wallClockIn(date, zone);
    }
    const locale = getDateFnsLocale(language);
    const pattern = INSTANT_PATTERNS[style];
    // Re-anchor the zone's wall clock as a *local* Date so date-fns — which only
    // ever reads local getters — prints those exact fields. For `date` we anchor
    // at local noon, which no DST transition can swallow.
    if (style === 'date') {
        return format(new Date(wall.year, wall.month - 1, wall.day, 12), pattern, { locale });
    }
    const anchor = new Date(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    if (anchor.getHours() === wall.hour && anchor.getDate() === wall.day) {
        return format(anchor, pattern, { locale });
    }
    // The wall time does not exist in the *process* zone (a spring-forward gap),
    // so no local Date can carry it and date-fns cannot render it. Fall back to
    // Intl, which formats straight from the instant. Correct to the minute; the
    // only cosmetic drift is Dutch abbreviated months ("30 apr" vs "30 apr.").
    return new Intl.DateTimeFormat(localeForLanguage(language), {
        ...INSTANT_INTL_OPTIONS[style],
        timeZone: zone,
    }).format(date);
}
/**
 * Resolves "what calendar day is this DB value?" **in an explicit IANA
 * timezone** and returns that day as a local-midnight `Date`.
 *
 * This is the calendar-day *arithmetic* counterpart to `formatInstantInZone`
 * (which renders). Use it any time you need `differenceInCalendarDays` /
 * same-day comparisons over a value that may be either column shape:
 *
 * - A `date` column (`'YYYY-MM-DD'`) carries no instant at all, so it passes
 *   straight through `parseLocalISODate` with **no zone math** — the day is
 *   already the answer.
 * - A `timestamptz` column (`'YYYY-MM-DD HH:MM:SS+TZ'` / ISO 8601) is a real
 *   instant, so its calendar day depends on who is asking. Reading the day off
 *   the UTC prefix is the bug this helper replaces: a journal entry picked as
 *   "Mar 15, 00:30" in Amsterdam is stored as `2026-03-14T23:30Z`, and a
 *   prefix read calls it Mar 14 — so it refuses to match a bank line booked
 *   Mar 15 even though both surfaces display the same date.
 *
 * The return value is a **local-midnight `Date`**, matching `parseLocalISODate`
 * output, so the two are directly comparable: date-fns only ever reads local
 * getters (`differenceInCalendarDays` truncates via `startOfDay`), so mixing a
 * zone-resolved day with a `date`-column day is safe.
 *
 * @param timeZone IANA zone, typically `organizations.timezone`. **Nullish**
 *   (org has no timezone set) resolves the day with the *process* getters — the
 *   browser-local behavior every caller had before, so nothing shifts for orgs
 *   that never configured a zone. An **unknown/garbage** zone string falls back
 *   to UTC rather than throwing (`Intl` raises `RangeError` on a bad zone and
 *   `organizations.timezone` is free text), mirroring `formatInstantInZone`.
 * @param onUnparseable Called when a non-empty value isn't a parseable date, so
 *   each host can report it its own way (the web app wires it to Sentry).
 */
export function dbCalendarDayInZone(value, timeZone, onUnparseable) {
    if (!value)
        return undefined;
    // `date` column: no instant, no zone math — the string *is* the calendar day.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const parsed = parseLocalISODate(value);
        if (!parsed)
            onUnparseable?.();
        return parsed;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
        // Non-empty input that isn't a date at all. Surfaces silent date-filter
        // regressions in the same class as the timestamptz bug this helper exists
        // for — callers commonly use a non-null assertion (`!`) downstream, so an
        // unhandled `undefined` either poisons a sort or silently drops a row.
        onUnparseable?.();
        return undefined;
    }
    if (timeZone == null) {
        // No org timezone configured: keep the pre-existing browser-local answer.
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
    let wall;
    try {
        wall = wallClockIn(date, timeZone);
    }
    catch {
        // Unknown IANA zone. UTC is the safe universal fallback: it is the zone the
        // database stores instants in and the zone the backend runs in.
        wall = wallClockIn(date, 'UTC');
    }
    // Re-anchor the zone's calendar day as a *local* midnight Date, so date-fns
    // (local getters only) and `parseLocalISODate` output line up exactly.
    return new Date(wall.year, wall.month - 1, wall.day);
}
/**
 * Offset of `timeZone` from UTC at instant `ms`, in ms (wall clock − UTC).
 * The wall clock is re-read as UTC with `setUTCFullYear`, not `Date.UTC`:
 * `Date.UTC` maps a year 0–99 to 1900–1999, which would put a 1st-century
 * day 1900 years off.
 */
function zoneOffsetMs(ms, timeZone) {
    const at = Math.floor(ms / 1000) * 1000;
    const w = wallClockIn(new Date(at), timeZone);
    const wallAsUtc = new Date(0);
    wallAsUtc.setUTCFullYear(w.year, w.month - 1, w.day);
    wallAsUtc.setUTCHours(w.hour, w.minute, w.second, 0);
    return wallAsUtc.getTime() - at;
}
/**
 * The first **instant** of calendar day `ymd` in IANA zone `timeZone`, as an
 * ISO-8601 UTC string (`2026-03-28T23:00:00.000Z` for Amsterdam's 29 March).
 *
 * The inverse direction of `dbCalendarDayInZone`: turn a calendar day the user
 * picked ("show me the 29th") into the `timestamptz` bound that day starts at
 * in the org's zone, so a `timestamptz` column can be filtered as
 * `>= start(from) AND < start(to + 1 day)`. DST-correct: the offset is read
 * at the candidate instants either side of the day, never assumed from the
 * process zone. On a day whose midnight does not exist (a spring-forward gap
 * AT midnight, e.g. America/Santiago), the day starts at the transition; on a
 * day whose midnight repeats, at the first one.
 *
 * @param timeZone IANA zone, typically `organizations.timezone`. Nullish or
 *   an unknown zone resolves in UTC (the `formatInstantInZone` fallback).
 * @returns `null` when `ymd` is not a real `YYYY-MM-DD` calendar day.
 */
export function zonedDayStartInstant(ymd, timeZone) {
    const days = ymdToDays(ymd);
    if (days === null)
        return null;
    let zone = timeZone ?? 'UTC';
    try {
        zonedPartsFormatter(zone).format(0);
    }
    catch {
        zone = 'UTC';
    }
    // `guess` = the day's midnight read as if it were UTC.
    const guess = days * 86_400_000;
    const DAY = 86_400_000;
    // The true start lies within ±14h of `guess`, so the offset in force there
    // is the one a day before or the one a day after (at most one transition
    // falls in between). Of the candidates that land on `ymd`, the earliest is
    // the day's start.
    const candidates = [guess - zoneOffsetMs(guess - DAY, zone), guess - zoneOffsetMs(guess + DAY, zone)];
    let start = null;
    for (const c of candidates) {
        const w = wallClockIn(new Date(c), zone);
        const onDay = `${String(w.year).padStart(4, '0')}-${pad2(w.month)}-${pad2(w.day)}` === ymd;
        if (onDay && (start === null || c < start))
            start = c;
    }
    return new Date(start ?? candidates[0]).toISOString();
}
/**
 * The last **instant** of calendar day `ymd` in IANA zone `timeZone`: one
 * millisecond before the next day starts there (`zonedDayStartInstant` of
 * `ymd + 1`). Use it to store "until the end of the 31st" as a `timestamptz`
 * that `formatOrgDay` renders back as the same day. DST-correct by
 * construction (the next day's start is resolved in-zone). Nullish or unknown
 * zone → UTC, like `zonedDayStartInstant`.
 *
 * @returns `null` when `ymd` is not a real `YYYY-MM-DD` calendar day.
 */
export function zonedDayEndInstant(ymd, timeZone) {
    if (ymdToDays(ymd) === null)
        return null;
    const nextDayStart = zonedDayStartInstant(addDaysYmd(ymd, 1), timeZone);
    return nextDayStart ? new Date(Date.parse(nextDayStart) - 1).toISOString() : null;
}
/* -------------------------------------------------------------------------- */
/*  Venue wall-clock values (no timezone)                                     */
/* -------------------------------------------------------------------------- */
//
// A third date shape beside calendar days (`date`) and instants
// (`timestamptz`): a reading off the venue's own clock, with no zone at all.
// `project_events.day_start/day_end` are Postgres `time`, and
// `project_event_items.starts_at/ends_at` are `timestamp` (WITHOUT time zone).
// On the wire they travel as:
//
//   - LocalTime  'HH:mm'             (e.g. '20:00')
//   - LocalStamp 'YYYY-MM-DDTHH:mm'  (e.g. '2026-06-12T23:30', no seconds, no zone)
//
// Every helper below is pure string/integer arithmetic. None of them builds a
// `Date`, calls `new Date(stamp)` or `toISOString()`: a `Date` would resolve
// the wall clock against the PROCESS timezone and shift it across a DST
// change or a UTC server. The org timezone does not scope these values either.
// Daylight-saving caveat: a performance that runs across a DST change has a
// real duration one hour off its wall-clock span; the planner accepts that.
/** Minutes in one calendar day. */
export const MINUTES_PER_DAY = 1440;
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_TIME_RE = /^(\d{2}):(\d{2})$/;
const LOCAL_STAMP_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/;
const DB_TIME_RE = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;
const DB_STAMP_RE = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;
/**
 * Days since 1970-01-01 for a proleptic-Gregorian civil date (Howard Hinnant's
 * `days_from_civil`) — integer arithmetic, no `Date`.
 */
function daysFromCivil(year, month, day) {
    const y = month <= 2 ? year - 1 : year;
    const era = Math.floor(y / 400);
    const yoe = y - era * 400;
    const mp = (month + 9) % 12;
    const doy = Math.floor((153 * mp + 2) / 5) + day - 1;
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
    return era * 146097 + doe - 719468;
}
/** Inverse of `daysFromCivil`. */
function civilFromDays(days) {
    const z = days + 719468;
    const era = Math.floor(z / 146097);
    const doe = z - era * 146097;
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
    const mp = Math.floor((5 * doy + 2) / 153);
    const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
    const month = mp < 10 ? mp + 3 : mp - 9;
    const year = yoe + era * 400 + (month <= 2 ? 1 : 0);
    return { year, month, day };
}
const pad2 = (n) => String(n).padStart(2, '0');
function ymdFromDays(days) {
    const { year, month, day } = civilFromDays(days);
    return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}`;
}
/** Days since 1970-01-01 for a real `YYYY-MM-DD` calendar day, else null. */
function ymdToDays(ymd) {
    const m = YMD_RE.exec(ymd);
    if (!m)
        return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1)
        return null;
    const days = daysFromCivil(year, month, day);
    // Round-trip rejects 2026-02-31 and friends.
    return ymdFromDays(days) === ymd ? days : null;
}
/** True when `ymd` is a `YYYY-MM-DD` string naming a real calendar day. */
export function isRealYmd(ymd) {
    return ymdToDays(ymd) !== null;
}
/**
 * Adds `days` (may be negative) to a `YYYY-MM-DD` calendar day. Throws on a
 * malformed or unreal date — callers pass validated wire values.
 */
export function addDaysYmd(ymd, days) {
    const base = ymdToDays(ymd);
    if (base === null)
        throw new RangeError(`addDaysYmd: not a calendar date: ${ymd}`);
    return ymdFromDays(base + Math.trunc(days));
}
/** Whole days from `from` to `to` (`to − from`); null when either is invalid. */
export function diffDaysYmd(from, to) {
    const a = ymdToDays(from);
    const b = ymdToDays(to);
    return a === null || b === null ? null : b - a;
}
/** `'HH:mm'` → minutes after midnight (0–1439); null when malformed. */
export function parseLocalTime(value) {
    if (!value)
        return null;
    const m = LOCAL_TIME_RE.exec(value);
    if (!m)
        return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h > 23 || min > 59)
        return null;
    return h * 60 + min;
}
/**
 * Minutes after midnight → `'HH:mm'`. Values outside one day wrap (1500 →
 * '01:00', −30 → '23:30'), which is what a clock face reads.
 */
export function minutesToLocalTime(minutes) {
    const m = ((Math.trunc(minutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}
/**
 * `'YYYY-MM-DDTHH:mm'` → `{ date, minutes }` (minutes after that day's
 * midnight). Null when malformed or when the date is not a real day.
 */
export function parseLocalStamp(value) {
    if (!value)
        return null;
    const m = LOCAL_STAMP_RE.exec(value);
    if (!m)
        return null;
    const date = m[1];
    const h = Number(m[2]);
    const min = Number(m[3]);
    if (h > 23 || min > 59 || !isRealYmd(date))
        return null;
    return { date, minutes: h * 60 + min };
}
/**
 * `(date, minutes)` → `'YYYY-MM-DDTHH:mm'`. Minutes outside 0–1439 roll into
 * the following (or preceding) days: `toLocalStamp('2026-06-12', 1500)` is
 * `'2026-06-13T01:00'`. Throws on an unreal date.
 */
export function toLocalStamp(date, minutes) {
    const m = Math.trunc(minutes);
    const dayOffset = Math.floor(m / MINUTES_PER_DAY);
    const inDay = m - dayOffset * MINUTES_PER_DAY;
    return `${addDaysYmd(date, dayOffset)}T${minutesToLocalTime(inDay)}`;
}
/**
 * Minutes from `from` to `to` (`to − from`) between two local stamps; null
 * when either is malformed. Works across midnight and month ends.
 */
export function diffLocalStampMinutes(from, to) {
    const a = parseLocalStamp(from);
    const b = parseLocalStamp(to);
    if (!a || !b)
        return null;
    const days = diffDaysYmd(a.date, b.date);
    if (days === null)
        return null;
    return days * MINUTES_PER_DAY + b.minutes - a.minutes;
}
/**
 * Whether two half-open `[starts_at, ends_at)` wall-clock spans overlap.
 * Touching is not overlapping (a 21:00 end and a 21:00 start are a clean
 * handover). False when any stamp is malformed. The ONE TS overlap rule: the
 * planner (`itemsOverlap`) and volunteer signup both use it; the SQL twin is
 * `tsrange(…, '[)') && tsrange(…, '[)')`.
 */
export function localStampsOverlap(a, b) {
    const aEndAfterBStart = diffLocalStampMinutes(b.starts_at, a.ends_at);
    const bEndAfterAStart = diffLocalStampMinutes(a.starts_at, b.ends_at);
    return aEndAfterBStart !== null && bEndAfterAStart !== null && aEndAfterBStart > 0 && bEndAfterAStart > 0;
}
/**
 * `stamp` shifted by `minutes` (may be negative), rolling across midnight and
 * month ends: `addLocalStampMinutes('2026-06-12T23:30', 90)` is
 * `'2026-06-13T01:00'`. Null when `stamp` is malformed.
 */
export function addLocalStampMinutes(stamp, minutes) {
    const s = parseLocalStamp(stamp);
    return s ? toLocalStamp(s.date, s.minutes + minutes) : null;
}
/**
 * Display form `'HH:mm'` of a local time, a local stamp, or minutes after
 * midnight (wrapped to the clock face). Returns `fallback` for null or
 * malformed input. Always the 24-hour venue clock — never localised through a
 * `Date`.
 */
export function formatLocalTime(value, fallback = '—') {
    if (value === null || value === undefined || value === '')
        return fallback;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? minutesToLocalTime(value) : fallback;
    }
    const time = parseLocalTime(value);
    if (time !== null)
        return minutesToLocalTime(time);
    const stamp = parseLocalStamp(value);
    return stamp ? minutesToLocalTime(stamp.minutes) : fallback;
}
export function normaliseDbTime(value) {
    if (value === null || value === undefined)
        return null;
    const m = DB_TIME_RE.exec(value);
    return m ? `${m[1]}:${m[2]}` : value;
}
export function normaliseDbStamp(value) {
    if (value === null || value === undefined)
        return null;
    const m = DB_STAMP_RE.exec(value);
    return m ? `${m[1]}T${m[2]}:${m[3]}` : value;
}
//# sourceMappingURL=date.js.map